/**
 * The seed a solo game runs on, rolled unless the URL pins one.
 *
 * A hosted game seeds from its join code, so every co-op session is somewhere new. A solo one has
 * no code to seed from, and for a long time it had a constant instead: one zone, every raid,
 * forever (#385). So it rolls one, out of the same alphabet a join code is drawn from, which makes
 * a solo seed a string a player can read off the HUD and hand to somebody else.
 *
 * `?seed=` still wins, and has to. A screenshot shot frames a fixed place and a soak grinds a fixed
 * one, so the thing that made those repeatable cannot be taken away by making a raid varied: it is
 * moved from a constant nobody chose to a query string that says what it is doing.
 *
 * This is still what a raid falls back to rather than what it runs on. The seed picker owns the
 * choice now: a URL naming a seed drops straight in, and a raid opened from the menu asks, with
 * this rolled seed already in the field. What is left here is the answer for a session that was
 * never asked, and for every mode that is not the raid.
 */

import { generateJoinCode } from '@/net/session/joinCode';

/** What pins a seed, and the name `raidRecipeUrl.ts` reads for the raid's own recipe. */
export const SEED_PARAM = 'seed';

/**
 * The pinned seed, or a fresh one.
 *
 * Whitespace is not a seed: a `?seed=` left empty is the same request as no `?seed=` at all, and
 * silently running a zone keyed on a blank string would be a worse answer than rolling.
 */
export function chooseSoloSeed(pinned: string | null, roll: () => string): string {
  const trimmed = pinned?.trim() ?? '';
  return trimmed === '' ? roll() : trimmed;
}

/** What the shell launches on. The DOM half, so `chooseSoloSeed` stays unit-testable. */
export function soloSeed(): string {
  const params = new URLSearchParams(window.location.search);
  return chooseSoloSeed(params.get(SEED_PARAM), generateJoinCode);
}
